import { Pencil } from 'lucide-react';
import { Button } from '../core/Button';
import type { WizardStep } from '../../types/wizard';
import { useWizard } from '../../context/WizardContext';
import { useTranslation } from '../../context/LanguageContext';
import { DEPARTMENT_NAME_KEYS } from '../../i18n';

function ReviewRow({ label, value, step }: Readonly<ReviewRowProps>) {
  const { goToStep } = useWizard();
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0 gap-4">
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</p>
        <p className="font-medium text-white mt-0.5 text-sm truncate">{value}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => goToStep(step)}
        disabled
        className="shrink-0 text-slate-400"
      >
        <Pencil className="h-3 w-3" />
        {t('common.edit')}
      </Button>
    </div>
  );
}

export function ReviewCard() {
  const { state } = useWizard();
  const { language, t } = useTranslation();

  const empty = t('common.emptyValue');

  function classValue(): string {
    if (state.selectedClass === 10) return t('wizard.reviewClassSsc');
    if (state.selectedClass === 12) return t('wizard.reviewClassHsc');
    return empty;
  }

  function examTypeValue(): string {
    if (state.selectedExamType === 'written') return t('common.written');
    if (state.selectedExamType === 'mcq') return t('common.mcq');
    return empty;
  }

  const rows: ReviewRowProps[] = [
    { label: t('wizard.reviewClass'), value: classValue(), step: 'class' },
    {
      label: t('wizard.reviewDepartment'),
      value: state.selectedDepartment
        ? t(DEPARTMENT_NAME_KEYS[state.selectedDepartment])
        : empty,
      step: 'department',
    },
    { label: t('wizard.reviewSubject'), value: state.selectedSubject?.name ?? empty, step: 'subject' },
    { label: t('wizard.reviewPaper'), value: state.selectedPaper?.name ?? empty, step: 'paper' },
    {
      label: t('wizard.reviewChapters'),
      value:
        state.selectedChapters.length > 0
          ? state.selectedChapters
              .map((c) => (language === 'bn' ? c.banglaName : c.name))
              .join(', ')
          : empty,
      step: 'chapter',
    },
    { label: t('wizard.reviewExamType'), value: examTypeValue(), step: 'examType' },
    {
      label: t('wizard.reviewQuestions'),
      value: state.numberOfQuestions ? String(state.numberOfQuestions) : empty,
      step: 'questions',
    },
    {
      label: t('wizard.reviewDuration'),
      value: state.duration ? `${state.duration} ${t('common.minutesShort')}` : empty,
      step: 'questions',
    },
    {
      label: t('wizard.reviewTotalMarks'),
      value: state.totalMarks ? String(state.totalMarks) : empty,
      step: 'questions',
    },
    { label: t('wizard.reviewVersion'), value: state.selectedVersion ?? empty, step: 'version' },
  ];

  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-800 overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-slate-700 bg-gradient-to-r from-violet-500/10 to-indigo-500/10">
        <h3 className="font-semibold text-white">{t('wizard.reviewCardTitle')}</h3>
        <p className="text-sm text-slate-400 mt-0.5">{t('wizard.reviewCardSubtitle')}</p>
      </div>
      <div className="px-5">
        {rows.map((row) => (
          <ReviewRow key={row.label} {...row} />
        ))}
      </div>
    </div>
  );
}

interface ReviewRowProps {
  label: string;
  value: string;
  step: WizardStep;
}
